import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { DollarSign, Lock, Unlock } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const CuadreDiario = () => {
  const [cuadre, setCuadre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [efectivoCaja, setEfectivoCaja] = useState(0);
  const [etiqueta, setEtiqueta] = useState('normal');

  useEffect(() => {
    fetchCuadreActual();
  }, []);

  const fetchCuadreActual = async () => {
    try {
      const response = await api.get('/cuadre/actual');
      if (response.data.id) {
        setCuadre(response.data);
      }
    } catch (error) {
      console.error('Error');
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirCuadre = async () => {
    try {
      await api.post('/cuadre/abrir');
      toast.success('Cuadre abierto');
      fetchCuadreActual();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleCerrarCuadre = async () => {
    if (!window.confirm('¿Cerrar el cuadre del día?')) return;
    try {
      await api.post('/cuadre/cerrar', { efectivo_caja: efectivoCaja, etiqueta });
      toast.success('Cuadre cerrado exitosamente');
      fetchCuadreActual();
    } catch (error) {
      toast.error('Error al cerrar cuadre');
    }
  };

  if (loading) return <div className="p-8 text-center">Cargando...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl flex items-center"><DollarSign className="mr-2" />Cuadre Diario</CardTitle>
              {!cuadre ? (
                <Button onClick={handleAbrirCuadre}><Unlock className="mr-2 h-4 w-4" />Abrir Cuadre</Button>
              ) : cuadre.estado === 'abierto' && (
                <Badge variant="default">Abierto</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!cuadre ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No hay cuadre abierto para hoy</p>
                <Button onClick={handleAbrirCuadre}>Abrir Cuadre del Día</Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Fecha</p>
                    <p className="text-2xl font-bold text-blue-600">{cuadre.fecha}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Usuario</p>
                    <p className="text-lg font-medium">{cuadre.usuario_nombre}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-gray-700">Total Cobros:</span>
                    <span className="text-xl font-bold text-green-600">${cuadre.total_cobros?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-gray-700">Total Suministros:</span>
                    <span className="text-xl font-bold text-blue-600">${cuadre.total_suministros?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                    <span className="text-gray-700">Total Gastos:</span>
                    <span className="text-xl font-bold text-red-600">-${cuadre.total_gastos?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-indigo-100 rounded-lg border-2 border-indigo-300">
                    <span className="text-lg font-semibold text-gray-800">BALANCE:</span>
                    <span className="text-2xl font-bold text-indigo-700">${cuadre.balance?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {cuadre.estado === 'abierto' && (
                  <div className="mt-6 p-4 border-t">
                    <div className="space-y-4">
                      <div>
                        <Label>Efectivo en Caja</Label>
                        <Input type="number" step="0.01" value={efectivoCaja} onChange={(e) => setEfectivoCaja(parseFloat(e.target.value) || 0)} placeholder="0.00" />
                      </div>
                      <div>
                        <Label>Etiqueta del Día</Label>
                        <Select value={etiqueta} onValueChange={setEtiqueta}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="bueno">Bueno</SelectItem>
                            <SelectItem value="excelente">Excelente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCerrarCuadre} className="w-full"><Lock className="mr-2 h-4 w-4" />Cerrar Cuadre del Día</Button>
                    </div>
                  </div>
                )}

                {cuadre.estado === 'cerrado' && (
                  <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                    <p className="text-center text-gray-600 font-medium">Cuadre cerrado a las {cuadre.hora_cierre}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CuadreDiario;
